/**
 * The ad network (ADR 0026, docs/runbooks/ads.md): AdSense on the web, AdMob in the Android app.
 * Nothing here loads unless /api/me hands over a network's ids, and every request asks for
 * non-personalised ads. Google's consent message comes with its script (web) or its form (app),
 * so it shows on the game pages only.
 */

export interface AdNetwork {
  test: boolean;
  adsense?: { client: string; bannerSlot?: string; h5: boolean };
  admob?: { banner?: string; interstitial?: string };
}

interface AdMobPlugin {
  initialize(o: { initializeForTesting?: boolean }): Promise<void>;
  requestConsentInfo(): Promise<{ status: string; isConsentFormAvailable?: boolean }>;
  showConsentForm(): Promise<unknown>;
  showBanner(o: Record<string, unknown>): Promise<void>;
  removeBanner(): Promise<void>;
  prepareInterstitial(o: Record<string, unknown>): Promise<unknown>;
  showInterstitial(): Promise<void>;
}
type AdsWindow = Window & {
  Capacitor?: { isNativePlatform?: () => boolean; Plugins?: { AdMob?: AdMobPlugin } };
  adsbygoogle?: unknown[] & { requestNonPersonalizedAds?: number };
  adBreak?: (o: Record<string, unknown>) => void;
  adConfig?: (o: Record<string, unknown>) => void;
};
const w = () => window as AdsWindow;

/** Inside the Android app (ADR 0007): ads come from AdMob, never AdSense (not allowed in apps). */
export const inApp = (): boolean => w().Capacitor?.isNativePlatform?.() === true;
const plugin = () => w().Capacitor?.Plugins?.AdMob ?? null;

let admobReady: Promise<AdMobPlugin> | null = null;
/** AdMob, started once: test mode on staging, and Google's consent form where it's required. */
function admob(net: AdNetwork): Promise<AdMobPlugin> {
  const p = plugin();
  if (!p) return Promise.reject(new Error("no AdMob plugin"));
  admobReady ??= (async () => {
    await p.initialize({ initializeForTesting: net.test });
    const consent = await p.requestConsentInfo();
    if (consent.isConsentFormAvailable && consent.status === "REQUIRED") await p.showConsentForm();
    return p;
  })();
  return admobReady;
}

/** The app's banner, at the top over the page's ad strip. Resolves to a function that removes it. */
export async function appBanner(net: AdNetwork, unit: string): Promise<() => void> {
  const p = await admob(net);
  await p.showBanner({
    adId: unit,
    adSize: "BANNER",
    position: "TOP_CENTER",
    margin: 0,
    isTesting: net.test,
    npa: true,
  });
  return () => {
    void p.removeBanner().catch(() => undefined);
  };
}

/** The app's full-screen ad; resolves once it's closed. */
export async function appInterstitial(net: AdNetwork, unit: string): Promise<void> {
  const p = await admob(net);
  await p.prepareInterstitial({ adId: unit, isTesting: net.test, npa: true });
  await p.showInterstitial();
}

let adsenseLoaded = false;
/** Resolves once AdSense's script has loaded (never, if it doesn't). */
let adsenseReady: Promise<void> = new Promise(() => undefined);
/** AdSense's script, once per page, asking for non-personalised ads only. */
export function loadAdSense(net: AdNetwork): void {
  const a = net.adsense;
  if (!a || adsenseLoaded) return;
  adsenseLoaded = true;
  const win = w();
  const queue = (win.adsbygoogle ??= Object.assign([], {}));
  queue.requestNonPersonalizedAds = 1;
  // The Ad Placement API (H5 Games): adBreak and adConfig queue onto adsbygoogle.
  win.adBreak = win.adConfig = (o) => {
    queue.push(o);
  };
  const s = document.createElement("script");
  s.async = true;
  s.crossOrigin = "anonymous";
  s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(a.client)}`;
  if (net.test && a.h5) s.dataset["adbreakTest"] = "on";
  adsenseReady = new Promise((resolve) => {
    s.addEventListener("load", () => {
      resolve();
    });
  });
  document.head.append(s);
  if (a.h5) win.adConfig({ preloadAdBreaks: "on", sound: "on" });
}

/** The parts of an `<ins class="adsbygoogle">` that decide whether to ask AdSense to fill it. */
export type AdUnit = Pick<Element, "isConnected" | "getAttribute">;
const asked = new WeakSet<AdUnit>();

/**
 * Ask AdSense to fill this unit, once: after its script has loaded, and only if the unit is still
 * on the page and not filled yet. Each push fills the next empty unit on the page, so a push for a
 * unit that's gone (a page change before the script loaded) or already filled throws "All 'ins'
 * elements ... already have ads in them" (Sentry, staging, 2026-10-22).
 */
export async function fillAdSense(
  ins: AdUnit,
  ready: Promise<void> = adsenseReady,
  queue: () => unknown[] | undefined = () => w().adsbygoogle,
): Promise<void> {
  await ready;
  if (asked.has(ins) || !ins.isConnected || ins.getAttribute("data-adsbygoogle-status")) return;
  asked.add(ins);
  queue()?.push({});
}

/** A full-screen web ad (H5 Games); `done` always runs, whether an ad showed or not. */
export function webAdBreak(name: string, done: (shown: boolean) => void): void {
  const brk = w().adBreak;
  if (!brk) {
    done(false);
    return;
  }
  brk({
    type: "next",
    name,
    adBreakDone: (info: { breakStatus?: string }) => {
      done(info.breakStatus === "viewed");
    },
  });
}
